import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { customToast } from '../../utils/customToast';
import {
  LayoutDashboard,
  Package,
  Users,
  ShoppingCart,
  Calculator,
  BarChart3,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  Settings,
  Search,
  ChevronDown,
  ChevronUp,
  Edit,
  Heart,
  Store,
  PanelLeftClose,
  PanelLeftOpen,
  Truck,
  ClipboardList,
  ExternalLink,
  HelpCircle,
  MessageCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { ProfileModal } from '../ProfileModal';
import { SupportTicketModal } from '../SupportTicketModal';
import { searchService, SearchResult } from '../../services/searchService';
import { SubscriptionBadge } from '../SubscriptionBadge';

// Utility function to truncate text with ellipsis
const truncateText = (text: string, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [sidebarProfileDropdownOpen, setSidebarProfileDropdownOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchDropdownPosition, setSearchDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isMobileSearchExpanded, setIsMobileSearchExpanded] = useState(false);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);
  const [logoImageFailed, setLogoImageFailed] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasModuleAccess } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { getSetting } = useSettings();

  // Helper function to get the correct logo URL
  const getLogoUrl = (): string => {
    const logoPath = getSetting('site_logo');
    if (!logoPath) return '';

    // If it's already a full URL (starts with http), return it
    if (logoPath.startsWith('http')) {
      return logoPath;
    }

    // If it contains /storage/ already, prepend API base
    if (logoPath.includes('/storage/')) {
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const baseUrl = apiBase.replace('/api', '');
      return `${baseUrl}${logoPath}`;
    }

    // Otherwise, construct with /storage/ prefix
    const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
    const baseUrl = apiBase.replace('/api', '');
    return `${baseUrl}/storage/${logoPath}`;
  };

  // Save collapsed state to localStorage
  const toggleSidebarCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
  };

  // Mobile viewport height fix
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Initial set
    setVH();

    // Update on resize and orientation change
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Helper function to get profile picture URL
  const getProfilePictureUrl = (user: any) => {
    if (user?.profile_picture_url) {
      return user.profile_picture_url;
    }
    if (user?.profile_picture) {
      // Check if it's already a full URL
      if (user.profile_picture.startsWith('http')) {
        return user.profile_picture;
      }
      // Otherwise, construct the URL using production API
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const storageBase = apiBase.replace('/api', '/storage');
      return `${storageBase}/${user.profile_picture}`;
    }
    return null;
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById('profile-dropdown');
      const sidebarDropdown = document.getElementById('sidebar-profile-dropdown');

      if (dropdown && !dropdown.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }

      if (sidebarDropdown && !sidebarDropdown.contains(event.target as Node)) {
        setSidebarProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen || sidebarProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [profileDropdownOpen, sidebarProfileDropdownOpen]);

  // Search functionality with debouncing
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
        setSelectedSearchIndex(-1);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Reset selected index when search results change
  React.useEffect(() => {
    setSelectedSearchIndex(-1);
  }, [searchResults]);

  // Keyboard navigation for search results
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showSearchResults || searchResults.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedSearchIndex(prev =>
            prev < searchResults.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedSearchIndex(prev => prev > 0 ? prev - 1 : -1);
          break;
        case 'Enter':
          event.preventDefault();
          if (selectedSearchIndex >= 0 && selectedSearchIndex < searchResults.length) {
            handleSearchResultClick(searchResults[selectedSearchIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          setShowSearchResults(false);
          setSelectedSearchIndex(-1);
          // Return focus to search input
          const activeInputRef = window.innerWidth < 640 && isMobileSearchExpanded
            ? mobileSearchInputRef.current
            : searchInputRef.current;
          if (activeInputRef) {
            activeInputRef.blur();
          }
          break;
      }
    };

    if (showSearchResults) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [showSearchResults, searchResults, selectedSearchIndex, isMobileSearchExpanded]);

  const performSearch = async (query: string) => {
    setIsSearching(true);
    try {
      const results = await searchService.search(query);
      setSearchResults(results);
      setShowSearchResults(results.length > 0 || query.length >= 2);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);

    // Calculate position for portal dropdown using the appropriate input ref
    const activeInputRef = window.innerWidth < 640 && isMobileSearchExpanded
      ? mobileSearchInputRef.current
      : searchInputRef.current;

    if (activeInputRef) {
      const rect = activeInputRef.getBoundingClientRect();
      setSearchDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // 8px margin
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  const handleSearchResultClick = (result: SearchResult) => {
    // Close dropdown and clear search immediately
    setShowSearchResults(false);
    setSearchQuery('');
    setSearchResults([]);
    setIsMobileSearchExpanded(false); // Also close mobile search

    // Navigate with search context based on result type
    switch (result.type) {
      case 'product':
        navigate(`/dashboard/products?search=${encodeURIComponent(result.title)}`);
        break;
      case 'customer':
        navigate(`/dashboard/customers?search=${encodeURIComponent(result.title)}`);
        break;
      case 'sale':
        navigate(`/dashboard/sales?search=${encodeURIComponent(`#${result.id}`)}`);
        break;
    }
  };

  // Handle click outside for search results
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const searchContainer = document.getElementById('search-container');
      const target = event.target as Element;

      // Check if click is inside search container
      if (searchContainer && searchContainer.contains(target)) {
        return;
      }

      // Check if click is inside the search dropdown (which is portaled to document.body)
      const searchDropdown = target.closest('[data-search-dropdown]');
      if (searchDropdown) {
        return;
      }

      // If click is outside both search container and dropdown, close the results
      setShowSearchResults(false);
    };

    if (showSearchResults) {
      // Use click instead of mousedown to avoid conflicts with button clicks
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSearchResults]);

  // Update dropdown position on scroll/resize
  useLayoutEffect(() => {
    const updateDropdownPosition = () => {
      if (showSearchResults) {
        const activeInputRef = window.innerWidth < 640 && isMobileSearchExpanded
          ? mobileSearchInputRef.current
          : searchInputRef.current;

        if (activeInputRef) {
          const rect = activeInputRef.getBoundingClientRect();
          setSearchDropdownPosition({
            top: rect.bottom + window.scrollY + 8,
            left: rect.left + window.scrollX,
            width: rect.width
          });
        }
      }
    };

    if (showSearchResults) {
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [showSearchResults, isMobileSearchExpanded]);

  // Update dropdown position when mobile search expands
  useEffect(() => {
    if (isMobileSearchExpanded && mobileSearchInputRef.current) {
      // Small delay to ensure the input is fully rendered
      setTimeout(() => {
        if (mobileSearchInputRef.current) {
          const rect = mobileSearchInputRef.current.getBoundingClientRect();
          setSearchDropdownPosition({
            top: rect.bottom + window.scrollY + 8,
            left: rect.left + window.scrollX,
            width: rect.width
          });
        }
      }, 50);
    }
  }, [isMobileSearchExpanded]);

  // Define all available menu items with their module mapping
  const allMenuItems = [
    { path: '/dashboard/overview', label: 'Dashboard', icon: LayoutDashboard, module: 'dashboard' },
    { path: '/dashboard/products', label: 'Products', icon: Package, module: 'products' },
    { path: '/dashboard/customers', label: 'Customers', icon: Users, module: 'customers' },
    { path: '/dashboard/pos', label: 'POS', icon: Calculator, module: 'pos' },
    { path: '/dashboard/sales', label: 'Sales', icon: ShoppingCart, module: 'sales' },
    { path: '/dashboard/suppliers', label: 'Suppliers', icon: Truck, module: 'suppliers' },
    { path: '/dashboard/purchase-orders', label: 'Purchase Orders', icon: ClipboardList, module: 'purchase_orders' },
    { path: '/dashboard/reports', label: 'Reports', icon: BarChart3, module: 'reports' },
    { path: '/dashboard/my-tickets', label: 'My Tickets', icon: MessageCircle, module: 'tickets' },
    { path: '/dashboard/configuration', label: 'Shop Config', icon: Store, module: 'shop_config' },
    { path: '/dashboard/settings', label: 'Settings', icon: Settings, module: 'settings' }, // Settings requires permission too
  ];

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item => {
    // Check if user has view permission for this module
    const hasAccess = hasModuleAccess(item.module, 'view');

    // Debug logging for staff members
    if (user?.role === 'shop_staff') {
      console.log(`🔐 Permission Check - Module: ${item.module}, Has Access: ${hasAccess}`);
    }

    return hasAccess;
  });

  // Log final menu for staff members
  React.useEffect(() => {
    if (user?.role === 'shop_staff') {
      console.log('📋 FILTERED MENU ITEMS:', menuItems.map(item => item.label));
      console.log('👤 User Permissions:', user?.permissions);
    }
  }, [user, menuItems]);

  const handleLogout = async () => {
    try {
      await logout();
      customToast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      customToast.error('Error logging out');
    }
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/dashboard/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div
      className="mobile-layout-container flex transition-all duration-300 h-screen w-screen overflow-hidden"
      style={{
        backgroundColor: theme === 'dark' ? '#0f172a' : '#f8fafc'
      }}
    >
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 transform transition-all duration-300 ease-in-out lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } w-72 lg:w-auto`}>
        <div className={`flex flex-col h-full transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-72'
          } w-72 ${theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-slate-200'
          } border-r group`}>
          {/* Logo */}
          <div className={`relative flex h-20 items-center justify-between border-b border-inherit px-6 transition-all duration-300 ${sidebarCollapsed ? 'lg:px-2' : ''
            }`}>
            <div className={`flex items-center space-x-3 transition-all duration-300 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl overflow-hidden ${theme === 'dark'
                ? 'bg-gradient-to-br from-blue-600 to-indigo-600'
                : 'bg-gradient-to-br from-blue-600 to-indigo-600'
                }`}>
                {getSetting('site_logo') && !logoImageFailed ? (
                  <img
                    src={getLogoUrl()}
                    alt="Site Logo"
                    className="w-full h-full object-cover"
                    onLoad={() => {
                      console.log('✅ Sidebar logo loaded successfully:', getSetting('site_logo'));
                    }}
                    onError={(e) => {
                      const logoUrl = getLogoUrl();
                      console.error('❌ Sidebar logo failed to load');
                      console.error('   URL:', logoUrl);
                      console.error('   Setting value:', getSetting('site_logo'));
                      console.error('   API Base URL:', import.meta.env.VITE_API_BASE_URL);
                      console.error('   Event type:', e.type);
                      console.error('   Target status:', (e.target as HTMLImageElement).complete);
                      console.error('   Full event:', e);
                      setLogoImageFailed(true);
                    }}
                  />
                ) : (
                  <Package className="h-6 w-6 text-white" />
                )}
              </div>
              <div>
                <h1 className={`text-heading text-lg font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                  }`} title={user?.shop?.shop_name || getSetting('site_name') || 'NextGen - Shop'}>
                  {user?.shop?.shop_name
                    ? truncateText(user.shop.shop_name, 17)
                    : truncateText(getSetting('site_name') || 'NextGen - Shop', 17)
                  }
                </h1>
                <p className={`text-caption truncate ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                  }`}>
                  Management System & POS
                </p>
              </div>
            </div>
            <div className={`hidden lg:flex mx-auto h-10 w-10 items-center justify-center rounded-xl overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'group-hover:opacity-0 group-hover:invisible' : 'lg:hidden'
              } ${theme === 'dark'
                ? 'bg-gradient-to-br from-blue-600 to-indigo-600'
                : 'bg-gradient-to-br from-blue-600 to-indigo-600'
              }`}>
              {getSetting('site_logo') && !logoImageFailed ? (
                <img
                  src={getLogoUrl()}
                  alt="Site Logo"
                  className="w-full h-full object-cover"
                  onError={() => {
                    const logoUrl = getLogoUrl();
                    console.error('❌ Collapsed sidebar logo failed to load');
                    console.error('   URL:', logoUrl);
                    console.error('   Setting value:', getSetting('site_logo'));
                    console.error('   API Base URL:', import.meta.env.VITE_API_BASE_URL);
                    setLogoImageFailed(true);
                  }}
                />
              ) : (
                <Package className="h-6 w-6 text-white" />
              )}
            </div>
            {/* Collapse Button - Desktop Only - Shows on hover */}
            <button
              onClick={toggleSidebarCollapse}
              className={`hidden lg:flex absolute top-1/2 -translate-y-1/2 items-center justify-center h-9 w-9 rounded-lg transition-all duration-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible ${sidebarCollapsed
                ? 'left-1/2 -translate-x-1/2'
                : 'right-2'
                } ${theme === 'dark'
                  ? 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                } shadow-sm`}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? (
                <PanelLeftOpen className="h-5 w-5" />
              ) : (
                <PanelLeftClose className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={() => setSidebarOpen(false)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${theme === 'dark'
                ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                }`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 overflow-y-auto py-6 space-y-2 px-4 transition-all duration-300 ${sidebarCollapsed ? 'lg:px-2' : ''}`}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    console.log('🔗 Shop menu clicked:', item.path);
                    console.log('📍 Current location:', location.pathname);
                    setSidebarOpen(false);
                  }}
                  title={sidebarCollapsed ? item.label : undefined}
                  className={`group flex items-center space-x-3 px-4 py-3 ${sidebarCollapsed ? 'lg:justify-center lg:w-10 lg:h-10 lg:mx-auto lg:p-0 lg:space-x-0' : ''
                    } rounded-xl text-sm font-medium transition-all duration-200 ${active
                      ? theme === 'dark'
                        ? 'bg-blue-900/30 text-blue-400 border border-blue-800/50'
                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                      : theme === 'dark'
                        ? 'text-slate-300 hover:text-white hover:bg-slate-700'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                >
                  <Icon className={`flex-shrink-0 h-5 w-5 transition-all duration-300 ${sidebarCollapsed ? 'lg:h-6 lg:w-6' : ''
                    } ${active
                      ? theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                      : theme === 'dark' ? 'text-slate-400 group-hover:text-slate-300' : 'text-slate-400 group-hover:text-slate-600'
                    }`} />
                  <span className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                    {item.label}
                  </span>
                  {active && (
                    <div className={`ml-auto h-2 w-2 rounded-full ${sidebarCollapsed ? 'lg:hidden' : ''
                      } ${theme === 'dark' ? 'bg-blue-400' : 'bg-blue-600'
                      }`} />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section - always at bottom */}
          <div className="flex-shrink-0 border-t border-inherit p-4 relative">
            <div className="relative">
              {/* Collapsed view - Desktop only */}
              <button
                onClick={() => setSidebarProfileDropdownOpen(!sidebarProfileDropdownOpen)}
                className={`hidden lg:flex w-full items-center justify-center rounded-xl transition-all duration-200 ${sidebarCollapsed ? '' : 'lg:hidden'
                  } ${sidebarProfileDropdownOpen
                    ? theme === 'dark'
                      ? 'ring-2 ring-blue-500/50'
                      : 'ring-2 ring-blue-500/20'
                    : ''
                  }`}
                title={user?.full_name || 'Profile'}
              >
                <div className="h-10 w-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white/10">
                  {getProfilePictureUrl(user) ? (
                    <img
                      src={getProfilePictureUrl(user)}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        const fallback = img.nextElementSibling as HTMLElement;
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className="w-full h-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center"
                    style={{ display: getProfilePictureUrl(user) ? 'none' : 'flex' }}
                  >
                    <span className="text-sm font-medium text-white">
                      {user?.full_name?.charAt(0).toUpperCase() || 'A'}
                    </span>
                  </div>
                </div>
              </button>

              {/* Expanded view - All screens when not collapsed, Mobile always */}
              <button
                onClick={() => setSidebarProfileDropdownOpen(!sidebarProfileDropdownOpen)}
                className={`flex w-full items-center space-x-3 p-3 rounded-xl transition-all duration-200 ${sidebarCollapsed ? 'lg:hidden' : ''
                  } ${sidebarProfileDropdownOpen
                    ? theme === 'dark'
                      ? 'bg-slate-700 ring-2 ring-blue-500/50'
                      : 'bg-slate-100 ring-2 ring-blue-500/20'
                    : theme === 'dark'
                      ? 'bg-slate-800/50 hover:bg-slate-700'
                      : 'bg-white hover:bg-slate-50'
                  } border ${theme === 'dark' ? 'border-slate-700' : 'border-slate-200'
                  } shadow-sm`}
              >
                <div className="h-10 w-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white/10">
                  {getProfilePictureUrl(user) ? (
                    <img
                      src={getProfilePictureUrl(user)}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        const fallback = img.nextElementSibling as HTMLElement;
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className="w-full h-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center"
                    style={{ display: getProfilePictureUrl(user) ? 'none' : 'flex' }}
                  >
                    <span className="text-sm font-medium text-white">
                      {user?.full_name?.charAt(0).toUpperCase() || 'A'}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                      }`} title={user?.full_name || 'Admin User'}>
                      {truncateText(user?.full_name || 'Admin User', 15)}
                    </p>
                    <SubscriptionBadge
                      subscriptionStatus={user?.shop?.subscription_status}
                      planType={user?.shop?.plan_type}
                      trialEndsAt={user?.shop?.trial_ends_at}
                      theme={theme}
                      size="sm"
                    />
                  </div>
                  <p className={`text-xs truncate ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                    }`} title={user?.username || 'admin'}>
                    {truncateText(user?.username || 'admin', 15)}
                  </p>
                </div>
                <ChevronUp className={`h-4 w-4 transition-transform duration-200 ${sidebarProfileDropdownOpen ? 'rotate-0' : 'rotate-180'
                  } ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
              </button>

              {/* Dropdown Menu */}
              {sidebarProfileDropdownOpen && (
                <div
                  id="sidebar-profile-dropdown"
                  className={`absolute bottom-full mb-2 py-2 rounded-xl shadow-xl border backdrop-blur-sm z-50 ${sidebarCollapsed ? 'left-full ml-2 w-48' : 'left-0 right-0'
                    } ${theme === 'dark'
                      ? 'bg-slate-800/95 border-slate-700'
                      : 'bg-white/95 border-slate-200'
                    }`}
                >
                  <button
                    onClick={() => {
                      setShowProfileModal(true);
                      setSidebarProfileDropdownOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left transition-colors ${theme === 'dark'
                      ? 'hover:bg-slate-700 text-slate-200'
                      : 'hover:bg-slate-50 text-slate-700'
                      }`}
                  >
                    <Edit className="h-4 w-4" />
                    <span className="text-sm">Edit Profile</span>
                  </button>

                  <div className={`my-1 h-px ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'
                    }`} />

                  <button
                    onClick={() => {
                      handleLogout();
                      setSidebarProfileDropdownOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left transition-colors ${theme === 'dark'
                      ? 'hover:bg-red-900/20 text-red-400'
                      : 'hover:bg-red-50 text-red-600'
                      }`}
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="text-sm">Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 grid grid-rows-[auto_1fr_auto] min-w-0 min-h-0 mobile-main-content overflow-hidden">
        {/* Top bar */}
        <header className={`flex h-16 sm:h-20 items-center transition-colors duration-200 ${theme === 'dark'
          ? 'bg-slate-800/80 border-slate-700'
          : 'bg-white/80 border-slate-200'
          } border-b backdrop-blur-xl`}>
          <div className="w-full px-4 sm:px-6">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className={`lg:hidden flex-shrink-0 p-2 rounded-lg transition-colors ${theme === 'dark'
                    ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                    }`}
                >
                  <Menu className="h-5 w-5" />
                </button>

                {/* Universal Search */}
                <div className="w-full max-w-md relative" style={{ zIndex: 2147483647 }} id="search-container">
                  <div className="relative">
                    {/* Mobile: Show only search icon when not expanded */}
                    <div className={`sm:hidden ${isMobileSearchExpanded ? 'hidden' : 'block'}`}>
                      <button
                        onClick={() => {
                          setIsMobileSearchExpanded(true);
                          // Focus and position will be handled by useEffect
                        }}
                        className={`p-2 rounded-lg transition-colors ${theme === 'dark'
                          ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                          }`}
                      >
                        <Search className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Mobile: Expanded search input */}
                    <div className={`sm:hidden ${isMobileSearchExpanded ? 'block' : 'hidden'}`}>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className={`h-4 w-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                              }`} />
                          </div>
                          <input
                            ref={mobileSearchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            placeholder="Search..."
                            autoFocus
                            className={`
                          w-full pl-9 pr-3 py-2 rounded-lg border transition-all duration-200 text-sm
                          ${theme === 'dark'
                                ? 'bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:bg-slate-700 focus:border-slate-500'
                                : 'bg-slate-50/50 border-slate-200 text-slate-900 placeholder-slate-500 focus:bg-white focus:border-slate-300'
                              } 
                          focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                        `}
                          />
                        </div>
                        <button
                          onClick={() => {
                            setIsMobileSearchExpanded(false);
                            setSearchQuery('');
                            setShowSearchResults(false);
                            setSearchResults([]);
                          }}
                          className={`p-2 rounded-lg transition-colors ${theme === 'dark'
                            ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                            }`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Desktop: Always show full search */}
                    <div className="hidden sm:block">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className={`h-5 w-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                          }`} />
                      </div>
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder="Search products, customers, sales..."
                        className={`
                      w-full pl-10 pr-4 py-2.5 rounded-xl border transition-all duration-200
                      ${theme === 'dark'
                            ? 'bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:bg-slate-700 focus:border-slate-500'
                            : 'bg-slate-50/50 border-slate-200 text-slate-900 placeholder-slate-500 focus:bg-white focus:border-slate-300'
                          } 
                      focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                    `}
                      />
                    </div>

                    {/* Search Results Dropdown moved to portal below */}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className={`p-2 rounded-xl transition-colors ${theme === 'dark'
                    ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                    }`}
                  title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                >
                  {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>

                {/* Landing Page Link - Hidden on mobile, icon only on small screens */}
                <button
                  onClick={() => window.open('/', '_blank', 'noopener,noreferrer')}
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-blue-500/20 focus:ring-offset-2 bg-blue-500 text-white hover:bg-blue-600"
                  title="Buy Now"
                  type="button"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span className="hidden md:inline">Buy Now</span>
                </button>

                {/* Help Button */}
                <button
                  onClick={() => setShowHelpModal(true)}
                  className={`p-2 rounded-xl transition-colors ${theme === 'dark'
                    ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                    }`}
                  title="Get Help"
                >
                  <HelpCircle className="h-5 w-5" />
                </button>

                {/* Profile Dropdown */}
                <div className="relative" id="profile-dropdown">
                  <button
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-xl transition-colors ${theme === 'dark'
                      ? 'text-slate-300 hover:text-white hover:bg-slate-700'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center overflow-hidden ${theme === 'dark'
                      ? 'bg-gradient-to-br from-slate-600 to-slate-700'
                      : 'bg-gradient-to-br from-slate-600 to-slate-700'
                      }`}>
                      {getProfilePictureUrl(user) ? (
                        <img
                          src={getProfilePictureUrl(user)}
                          alt="Profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            img.style.display = 'none';
                            const fallback = img.nextElementSibling as HTMLElement;
                            if (fallback) {
                              fallback.style.display = 'block';
                            }
                          }}
                        />
                      ) : null}
                      <span
                        className="text-xs font-medium text-white"
                        style={{ display: getProfilePictureUrl(user) ? 'none' : 'block' }}
                      >
                        {user?.full_name?.charAt(0).toUpperCase() || 'A'}
                      </span>
                    </div>
                    <div className="hidden sm:block text-left">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                          }`} title={user?.full_name || 'Admin User'}>
                          {truncateText(user?.full_name || 'Admin User', 20)}
                        </p>
                        {user?.username === 'abu.pos' && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${theme === 'dark'
                            ? 'bg-purple-900/50 text-purple-200 border border-purple-700'
                            : 'bg-purple-100 text-purple-700 border border-purple-200'
                            }`}>
                            Demo
                          </span>
                        )}
                        <SubscriptionBadge
                          subscriptionStatus={user?.shop?.subscription_status}
                          planType={user?.shop?.plan_type}
                          trialEndsAt={user?.shop?.trial_ends_at}
                          theme={theme}
                          size="sm"
                        />
                      </div>
                      <p className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                        }`} title={user?.email || 'admin@grocerystore.com'}>
                        {truncateText(user?.email || 'admin@grocerystore.com', 25)}
                      </p>
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${profileDropdownOpen ? 'rotate-180' : ''
                      }`} />
                  </button>

                  {/* Dropdown Menu */}
                  {profileDropdownOpen && (
                    <div className={`absolute right-0 mt-2 w-48 rounded-xl shadow-lg border z-50 ${theme === 'dark'
                      ? 'bg-slate-800 border-slate-700'
                      : 'bg-white border-slate-200'
                      }`}>
                      <div className="p-1">
                        <button
                          onClick={() => {
                            setShowProfileModal(true);
                            setProfileDropdownOpen(false);
                          }}
                          className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${theme === 'dark'
                            ? 'text-slate-300 hover:text-white hover:bg-slate-700'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                            }`}
                        >
                          <Edit className="h-4 w-4" />
                          <span>Edit Profile</span>
                        </button>

                        <div className={`my-1 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-slate-200'
                          }`} />

                        <button
                          onClick={() => {
                            handleLogout();
                            setProfileDropdownOpen(false);
                          }}
                          className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${theme === 'dark'
                            ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20'
                            : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                            }`}
                        >
                          <LogOut className="h-4 w-4" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="overflow-y-auto" style={{ zIndex: 'auto' }}>
          <div className="p-4 sm:p-6" style={{ zIndex: 'auto' }}>
            <div className="max-w-7xl mx-auto animate-fade-in">
              {children}
            </div>
          </div>
        </main>

        {/* Footer - always at viewport bottom */}
        <footer className={`py-3 px-4 sm:py-4 sm:px-6 border-t ${theme === 'dark' ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'
          }`}>
          <div className="flex flex-col space-y-2 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
            <p className={`text-xs flex items-center justify-center sm:justify-start ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
              }`}>
              Built with{' '}
              <Heart className="animate-heartbeat mx-1 h-3 w-3 text-red-500 fill-current" />
              {' '}by{' '}
              <a
                href="https://www.hindukushsoft.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline transition-colors text-blue-600 hover:text-blue-800 ml-1"
              >
                HindukushSoft Technologies
              </a>
            </p>
            <p className={`text-xs text-center sm:text-right ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
              }`}>
              © {new Date().getFullYear()} {getSetting('site_name') || 'NextGen - Shop'}. All rights reserved.
            </p>
          </div>
        </footer>
      </div>

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />

      {/* Search Results Dropdown Portal */}
      {showSearchResults && createPortal(
        <div
          className="fixed inset-0 z-[9999999] pointer-events-none"
        >
          <div
            data-search-dropdown
            onClick={(e) => e.stopPropagation()}
            className={`absolute pointer-events-auto animate-in fade-in-0 zoom-in-95 duration-200 rounded-xl border shadow-2xl overflow-y-auto ${theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-slate-200'
              }`}
            style={{
              top: window.innerWidth < 640 ? (searchDropdownPosition.top > 0 ? searchDropdownPosition.top : 72) : searchDropdownPosition.top,
              left: window.innerWidth < 640 ? 16 : Math.max(16, Math.min(searchDropdownPosition.left, window.innerWidth - Math.min(448, window.innerWidth - 32))),
              transform: 'none',
              width: Math.min(Math.max(searchDropdownPosition.width, 384), window.innerWidth - 32),
              maxHeight: window.innerWidth < 640 ? Math.min(320, window.innerHeight - (searchDropdownPosition.top > 0 ? searchDropdownPosition.top : 72) - 16) : Math.min(384, window.innerHeight - searchDropdownPosition.top - 40)
            }}
          >
            {isSearching ? (
              <div className="p-4 sm:p-6 text-center">
                <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-2 border-blue-600/20"></div>
                    <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-2 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
                  </div>
                  <span className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                    Searching...
                  </span>
                </div>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="py-1 sm:py-2">
                <div className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs font-semibold tracking-wide uppercase ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                  }`}>
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                </div>
                {searchResults.map((result, index) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSearchResultClick(result);
                    }}
                    className={`w-full px-3 sm:px-4 py-3 sm:py-4 text-left flex items-center space-x-3 sm:space-x-4 transition-all duration-150 ease-out group touch-manipulation ${index === selectedSearchIndex
                      ? 'bg-blue-100 dark:bg-blue-900/50'
                      : 'hover:bg-slate-100/80 dark:hover:bg-slate-700/50 active:bg-slate-200/80 dark:active:bg-slate-600/50'
                      } ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                      } ${index !== searchResults.length - 1 ? 'border-b border-slate-100/60 dark:border-slate-700/40' : ''}`}
                  >
                    <div className={`flex-shrink-0 w-3 h-3 rounded-full shadow-sm ${result.type === 'product' ? 'bg-green-500' :
                      result.type === 'customer' ? 'bg-blue-500' : 'bg-orange-500'
                      }`}></div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold truncate transition-colors ${index === selectedSearchIndex
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'group-hover:text-blue-600 dark:group-hover:text-blue-400'
                        }`}>
                        {result.title}
                      </div>
                      <div className={`text-xs sm:text-sm truncate mt-0.5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                        }`}>
                        {result.subtitle}
                      </div>
                    </div>
                    <div className={`text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-full capitalize font-medium shadow-sm ${result.type === 'product'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      result.type === 'customer'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                      }`}>
                      {result.type}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 sm:p-6 text-center">
                <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-100'
                    }`}>
                    <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className={`text-sm sm:font-medium ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>
                      No results found
                    </div>
                    <div className={`text-xs sm:text-sm mt-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                      Try adjusting your search terms
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Support Ticket Modal */}
      <SupportTicketModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
    </div>
  );
};

export default Layout;